<template>
	<div class="square-m" :aria-busy="pageDataPending">
		<PageSkeleton v-if="showPageSkeleton" />
		<template v-else>
			<!-- 播客 -->
			<Recommend />

			<!-- <div v-if="displayBanners.length" class="banner" :ref="bindBannerRef">
			<SquareBannerCarousel
				:slides="displayBanners"
				:locale="locale"
				@banner-click="handleBannerClick"
			/>
		</div> -->

			<!-- shorts -->
			<section class="content-section">
				<div class="section-header">
					<span class="video-section-title">{{ t("square.shorts.title") }}</span>
					<div class="more-btn" @click="goToShortsMore">
						<span>{{ t("square.shorts.more") }} </span>
					</div>
				</div>

				<div class="video-section">
					<div
						v-for="(item, index) in shortsList"
						:key="item.id ?? index"
						class="video-section-card"
						:ref="el => bindShortCardRef(el, item, index)"
						@click="goToSquareDetail(item, 'short', index)"
					>
						<img class="video-section-card-cover" :src="item.cover" alt="" />
						<div class="video-section-card-title">{{ item.title }}</div>
						<div class="video-section-card-meta">
							<img class="video-section-card-meta-price" src="../images/price.png" alt="" />
							<span class="video-section-card-meta-author">{{
								item.nickname || t("square.article.defaultAuthor")
							}}</span>
							<img class="video-section-card-meta-eye" src="../images/eye.png" alt="" />
							<span>{{ formatViewCount(item.view_count) }}</span>
						</div>
						<img
							class="video-section-card-play"
							src="../images/vedio2.png"
							alt=""
							@click.stop="handleVideoPlayClick(item, 'short', index)"
						/>
					</div>
				</div>
			</section>

			<!-- 图文 -->
			<section class="content-section">
				<div class="section-header">
					<span class="video-section-title">{{ t("square.article.title") }}</span>
					<div class="more-btn" @click="goToArticleMore">
						<span>{{ t("square.article.more") }} </span>
					</div>
				</div>

				<div class="video-section">
					<div
						v-for="(item, index) in tuwenData"
						:key="item.id ?? index"
						class="video-section-card"
						:ref="el => bindArticleCardRef(el, item, index)"
						@click="goToSquareDetail(item, 'article', index)"
					>
						<img class="video-section-card-cover" :src="item.cover" alt="" />
						<div class="video-section-card-title">{{ item.title }}</div>
						<div class="video-section-card-meta">
							<img class="video-section-card-meta-price" src="../images/price.png" alt="" />
							<span class="video-section-card-meta-author">{{
								item.nickname || t("square.article.defaultAuthor")
							}}</span>
							<img class="video-section-card-meta-eye" src="../images/eye.png" alt="" />
							<span>{{ formatViewCount(item.view_count) }}</span>
						</div>
					</div>
				</div>
			</section>

			<div v-if="!mediaFeedExpanded" class="media-feed-trigger" @click="expandMediaFeed">
				{{ t("square.load.showMore") }}
			</div>

			<!-- 更多 -->
			<div v-if="mediaFeedExpanded" class="media-feed">
				<div
					v-for="(item, index) in mediaFeedList"
					:key="item.id ?? `media-${index}`"
					class="video-item"
					:ref="el => bindPodcastCardRef(el, item, index)"
					@click="goToSquareDetail(item, 'podcast', index)"
				>
					<img class="video-item-banner" :src="item.cover" alt="" />
					<img
						class="video-item-play"
						src="../images/vedio2.png"
						alt=""
						@click.stop="handleVideoPlayClick(item, 'podcast', index)"
					/>
					<div class="video-item-title">
						<img :src="item.avatar" alt="" />
						<span>{{ item.title }}</span>
					</div>
					<div class="video-item-author">
						<span class="video-item-author-name">{{ item.nickname }}</span>
						<img src="../images/eye.png" alt="" />
						<span class="video-item-author-view">{{ formatViewCount(item.view_count) }}</span>
					</div>
					<div
						v-if="item.media_second_str || item.media_second != null"
						class="video-item-duration"
					>
						{{ item.media_second_str || formatSquareMediaTime(item.media_second) }}
					</div>
				</div>

				<div ref="loadMoreSentinel" class="media-feed__sentinel" aria-hidden="true"></div>

				<div v-if="mediaLoading" class="media-feed__status">{{ t("square.load.loading") }}</div>
				<div v-else-if="mediaFinished && mediaFeedList.length > 0" class="media-feed__status">
					{{ t("square.load.done") }}
				</div>
			</div>
		</template>
	</div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, provide, ref, watch } from "vue";
import { storeToRefs } from "pinia";
import { getSquareBanner, getSquareMediaList, playContent } from "~/api/api";
import { removeDomain } from "~/utils/common";
import { useUserStore } from "~/stores/user";
import { useI18n } from "~/composables/useI18n";
import { COLD_START_TAGS_TAB_SWITCH_EVENT } from "~/utils/coldStartTags";
import eventBus from "~/utils/eventBus";
import Recommend from "./components/recommend/index.vue";
import PageSkeleton from "./components/page-skeleton/index.vue";
import SquareBannerCarousel from "../pc/components/banner-carousel/index.vue";
import { formatSquareDate, formatSquareMediaTime } from "~/utils/squareFormat.js";
import { useSquarePageData } from "@variants/seer/square/composables/useSquarePageData";
import {
	buildContentParams,
	MEDIA_PAGE_NAMES,
	MEDIA_TYPES,
	useMediaBannerImpression,
	useMediaCardImpression,
	useMediaSquareTracking,
} from "../composables/useMediaSquareTracking";

const SQUARE_MEDIA_REFRESH_EVENT = "square-media-refresh";
const userStore = useUserStore();
const { isLogin } = storeToRefs(userStore);
const { t, locale } = useI18n();

const mediaTracking = useMediaSquareTracking({
	deviceType: "mobile",
	pageName: MEDIA_PAGE_NAMES.HOME,
});
const { bindBannerRef } = useMediaBannerImpression({ track: mediaTracking.track });

const impressionParams = (item, index) =>
	buildContentParams(item, {
		position: index,
		formatTime: formatSquareMediaTime,
		authorFallback: t("square.article.defaultAuthor"),
	});

const { bindCardRef: bindShortCardRef } = useMediaCardImpression({
	track: mediaTracking.track,
	impressionEvent: "short_card_impression",
	getItemParams: impressionParams,
});
const { bindCardRef: bindArticleCardRef } = useMediaCardImpression({
	track: mediaTracking.track,
	impressionEvent: "article_card_impression",
	getItemParams: impressionParams,
});
const { bindCardRef: bindPodcastCardRef } = useMediaCardImpression({
	track: mediaTracking.track,
	impressionEvent: "podcast_card_impression",
	getItemParams: impressionParams,
});

provide("mediaSquareTracking", mediaTracking);

const { data: bannerList } = await useLazyAsyncData(
	"square-m-banner",
	async () => {
		const res = await getSquareBanner({
			page: 1,
			is_media: 1,
		});
		const list = res?.data?.top_banner;
		return Array.isArray(list) ? list : [];
	},
	{
		default: () => [],
		server: true,
	}
);

const displayBanners = computed(() => bannerList.value || []);

const resolveBannerUrl = item => {
	const raw = item?.actioncontent?.global_type || "";
	if (!raw) return "";

	const deeplinkMatch = raw.match(/^comxylling:\/\/(.+)$/i);
	const url = deeplinkMatch ? deeplinkMatch[1] : raw;

	if (/^https?:\/\//i.test(url)) {
		return url;
	}

	if (url.startsWith("/")) {
		return url;
	}

	return removeDomain(url, 1) || url;
};

const handleBannerClick = (item, index) => {
	const targetUrl = resolveBannerUrl(item);
	mediaTracking.trackBannerClick({
		bannerId: String(item?.id || "media_square_promo"),
		targetUrl,
	});

	if (!targetUrl) return;

	if (/^https?:\/\//i.test(targetUrl)) {
		window.location.href = targetUrl;
		return;
	}

	navigateTo(targetUrl);
};

const {
	data: pageData,
	refresh: refreshPageData,
	pending: pageDataPending,
} = useSquarePageData("mobile");

const showPageSkeleton = computed(
	() =>
		pageDataPending.value &&
		!(pageData.value?.shorts?.length || pageData.value?.imageTextList?.length)
);

const shortsList = computed(() => (pageData.value?.shorts || []).slice(0, 4));
const tuwenData = computed(() => (pageData.value?.imageTextList || []).slice(0, 4));

const goToShortsMore = () => {
	mediaTracking.trackMediaMoreClick({
		sectionId: "shorts",
		mediaType: MEDIA_TYPES.SHORT,
		currentCount: shortsList.value.length,
	});
	navigateTo({ path: "/seer/square/article", query: { tab: "2" } });
};

const goToArticleMore = () => {
	mediaTracking.trackMediaMoreClick({
		sectionId: "article",
		mediaType: MEDIA_TYPES.ARTICLE,
		currentCount: tuwenData.value.length,
	});
	navigateTo({ path: "/seer/square/article", query: { tab: "3" } });
};

const mediaFeedList = ref([]);
const mediaFeedExpanded = ref(false);
const mediaPage = ref(3);
const mediaLoading = ref(false);
const mediaFinished = ref(false);
const loadMoreSentinel = ref(null);
let loadObserver = null;

const formatViewCount = count => {
	const value = Number(count) || 0;
	if (value >= 10000) {
		if (locale.value === "en") {
			return t("square.views.count", { count: `${(value / 1000).toFixed(1)}K` });
		}
		return t("square.views.tenThousand", { count: (value / 10000).toFixed(1) });
	}
	return t("square.views.count", { count: value });
};

const toSquareDetail = async item => {
	const id = item?.id;
	if (id == null || id === "") return;
	await navigateTo(`/seer/square-detail/${id}`);
	await playContent({
		square_id: id,
	});
};

provide("formatTime", formatSquareMediaTime);
provide("formatDate", formatSquareDate);
provide("toSquareDetail", toSquareDetail);

const goToSquareDetail = (item, mediaType, index = 0) => {
	if (mediaType === MEDIA_TYPES.SHORT) {
		mediaTracking.trackShortCardClick(item, index, t("square.article.defaultAuthor"));
	} else if (mediaType === MEDIA_TYPES.ARTICLE) {
		mediaTracking.trackArticleCardClick(
			item,
			index,
			formatSquareMediaTime,
			t("square.article.defaultAuthor")
		);
	} else if (mediaType === MEDIA_TYPES.PODCAST) {
		mediaTracking.trackPodcastCardClick(
			item,
			index,
			formatSquareMediaTime,
			t("square.podcast.defaultAuthor")
		);
	}
	toSquareDetail(item);
};

const handleVideoPlayClick = (item, mediaType, index) => {
	mediaTracking.trackVideoPlayClick(item, {
		mediaType,
		position: index,
		formatTime: formatSquareMediaTime,
		authorFallback: t("square.article.defaultAuthor"),
	});
	toSquareDetail(item);
};

const resetMediaFeed = () => {
	mediaFeedExpanded.value = false;
	mediaFeedList.value = [];
	mediaPage.value = 3;
	mediaFinished.value = false;
	loadObserver?.disconnect();
	loadObserver = null;
};

const expandMediaFeed = async () => {
	if (mediaFeedExpanded.value || mediaLoading.value) return;
	mediaTracking.trackMediaMoreClick({
		sectionId: "media_feed",
		mediaType: MEDIA_TYPES.PODCAST,
		currentCount: shortsList.value.length + tuwenData.value.length,
	});
	mediaFeedExpanded.value = true;
	await nextTick();
	setupLoadObserver();
	await loadMediaFeed();
};

const extractMediaListItems = payload => {
	if (!payload || typeof payload !== "object") return [];
	if (Array.isArray(payload.top_content)) return payload.top_content;
	if (Array.isArray(payload.list)) return payload.list;
	if (Array.isArray(payload.data)) return payload.data;
	return [];
};

const loadMediaFeed = async () => {
	if (mediaLoading.value || mediaFinished.value) return;

	mediaLoading.value = true;
	try {
		const res = await getSquareMediaList({
			page: mediaPage.value,
			is_media: 1,
		});
		const data = res?.data || {};
		const list = extractMediaListItems(data);
		const pager = data.pager || res?.pager || {};

		if (list.length > 0) {
			mediaFeedList.value.push(...list);
		}

		const lastPage = Number(pager.last_page) || 0;
		const currentPage = Number(pager.current_page) || mediaPage.value;

		if (list.length === 0 || (lastPage > 0 && currentPage >= lastPage)) {
			mediaFinished.value = true;
		} else {
			mediaPage.value += 1;
		}
	} catch (error) {
		console.error("加载广场媒体列表失败:", error);
		mediaFinished.value = true;
	} finally {
		mediaLoading.value = false;
	}
};

const setupLoadObserver = () => {
	if (!import.meta.client || !loadMoreSentinel.value) return;

	loadObserver = new IntersectionObserver(
		entries => {
			if (entries.some(entry => entry.isIntersecting)) {
				loadMediaFeed();
			}
		},
		{ root: null, rootMargin: "120px 0px", threshold: 0 }
	);
	loadObserver.observe(loadMoreSentinel.value);
};

const handleSquareMediaRefresh = async () => {
	const wasExpanded = mediaFeedExpanded.value;
	resetMediaFeed();
	await refreshPageData();
	if (wasExpanded) {
		await expandMediaFeed();
	}
};

const checkColdStartTagsDialog = async () => {
	if (!import.meta.client) return;

	const hasToken = Boolean(localStorage.getItem("token"));
	if (!isLogin.value && !hasToken) return;

	if (!isLogin.value && hasToken) {
		await userStore.refreshLoginStatus();
	}

	if (!isLogin.value) return;

	eventBus.emit(COLD_START_TAGS_TAB_SWITCH_EVENT);
};

const handleLoginSuccess = () => {
	void checkColdStartTagsDialog();
};

const isPageReload = () => {
	if (!import.meta.client) return false;
	const navigation = performance.getEntriesByType("navigation")?.[0];
	return navigation?.type === "reload";
};

onMounted(() => {
	mediaTracking.trackMediaPageView();
	void checkColdStartTagsDialog();
	eventBus.on(SQUARE_MEDIA_REFRESH_EVENT, handleSquareMediaRefresh);
	eventBus.on("login-success", handleLoginSuccess);
	if (isPageReload()) {
		refreshNuxtData();
	}
});

onBeforeUnmount(() => {
	eventBus.off(SQUARE_MEDIA_REFRESH_EVENT, handleSquareMediaRefresh);
	eventBus.off("login-success", handleLoginSuccess);
	loadObserver?.disconnect();
});

watch(isLogin, (loggedIn, wasLoggedIn) => {
	if (loggedIn && wasLoggedIn === false) {
		void checkColdStartTagsDialog();
	}
});
</script>

<style lang="scss" scoped>
@mixin text-clamp($lines: 2) {
	display: -webkit-box;
	-webkit-box-orient: vertical;
	-webkit-line-clamp: $lines;
	line-clamp: $lines;
	overflow: hidden;
	text-overflow: ellipsis;
}

.square-m {
	position: relative;
	width: 750px;
	overflow: hidden;
	padding-bottom: 40px;

	.banner {
		width: 654px;
		margin: 50px auto 20px;

		:deep(.banner-slide__img),
		:deep(.banner-slide__lottie) {
			border-radius: 12px;
		}
	}

	.section-header {
		margin: 40px 0 20px;
		display: flex;
		justify-content: space-between;
		align-items: center;

		.video-section-title {
			font-size: 36px;
			color: #333333;
			font-weight: 500;
		}

		.more-btn {
			font-size: 28px;
			color: #595959;
			border: 1px solid #e5e5e5;
			padding: 5px 20px;
			border-radius: 8px;
		}
	}

	.content-section {
		padding: 0 30px;
	}

	.video-section {
		display: grid;
		grid-template-columns: repeat(2, minmax(0, 1fr));
		gap: 18px;
		overflow: visible;
		width: 100%;
		box-sizing: border-box;

		.video-section-card {
			width: 100%;
			background: #fff;
			border: 1px solid #efefef;
			border-radius: 16px;
			overflow: hidden;
			position: relative;
			cursor: pointer;

			.video-section-card-cover {
				width: 100%;
				height: 456px;
				object-fit: cover;
			}

			.video-section-card-title {
				margin: 10px 0 20px;
				padding: 0 10px;
				font-size: 28px;
				font-weight: 500;
				color: #333333;
				@include text-clamp(2);
			}

			.video-section-card-meta {
				padding: 0 10px 20px;
				display: flex;
				justify-content: space-between;
				align-items: center;
				font-size: 24px;
				color: #595959;

				.video-section-card-meta-price {
					width: 36px;
					height: 36px;
					margin-right: 10px;
				}

				.video-section-card-meta-author {
					font-weight: 500;
					flex: 1;
					overflow: hidden;
					text-overflow: ellipsis;
					white-space: nowrap;
				}

				.video-section-card-meta-eye {
					width: 24px;
					height: 24px;
					margin-right: 8px;
				}
			}

			.video-section-card-play {
				width: 48px;
				height: 48px;
				position: absolute;
				right: 20px;
				top: 20px;
			}
		}
	}

	.media-feed-trigger {
		width: 654px;
		margin: 40px auto 0;
		padding: 24px 0;
		text-align: center;
		font-size: 28px;
		color: #666666;
		cursor: pointer;
		user-select: none;
	}

	.media-feed {
		width: 654px;
		margin: 40px auto 0;

		.video-item {
			width: 100%;
			border-radius: 12px;
			border: 1px solid #ececec;
			padding: 16px 16px 18px;
			box-sizing: border-box;
			margin-bottom: 20px;
			position: relative;
			cursor: pointer;

			.video-item-banner {
				width: 100%;
				height: 350px;
				border-radius: 8px;
				object-fit: cover;
				display: block;
			}

			.video-item-play {
				width: 50px;
				height: 50px;
				position: absolute;
				right: 32px;
				top: 32px;
			}

			.video-item-title {
				width: 100%;
				margin: 20px 0;
				display: flex;
				align-items: center;
				gap: 12px;

				img {
					width: 60px;
					height: 60px;
					border-radius: 50%;
					flex-shrink: 0;
					object-fit: cover;
				}

				span {
					flex: 1;
					color: #333333;
					font-size: 28px;
					font-weight: 500;
					@include text-clamp(2);
				}
			}

			.video-item-author {
				width: 100%;
				display: flex;
				align-items: center;
				gap: 10px;

				.video-item-author-name {
					color: #333333;
					font-size: 28px;
					font-weight: 500;
					margin-left: 70px;
				}

				img {
					width: 26px;
					height: 26px;
					object-fit: cover;
				}

				.video-item-author-view {
					color: #9e9e9e;
					font-size: 24px;
				}
			}

			.video-item-duration {
				background: rgba(0, 0, 0, 0.58);
				border-radius: 4px;
				height: 36px;
				min-width: 78px;
				position: absolute;
				right: 24px;
				top: 320px;
				padding: 0 10px;
				box-sizing: border-box;
				color: #ffffff;
				font-size: 22px;
				display: flex;
				align-items: center;
				justify-content: center;
				white-space: nowrap;
				line-height: 1;
			}
		}

		&__sentinel {
			height: 1px;
		}

		&__status {
			padding: 24px 0 8px;
			text-align: center;
			font-size: 24px;
			color: #999999;
		}
	}
}
</style>
